"use client";

import { useEffect, useState } from "react";
import { WebsiteNav } from "@/components/website-nav";
import Link from "next/link";
import { blogApi, type BlogPostSummary } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function BlogListingPage() {
  const [posts, setPosts] = useState<BlogPostSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    blogApi.getAllPosts()
      .then(setPosts)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="min-h-screen bg-[#F3F6FA]">
      <WebsiteNav />

      {/* Hero */}
      <section className="border-b border-[#DCE3EA] bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-14 sm:px-8 lg:px-12 lg:py-20">
          <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
            <div>
              <p className="mono text-[10px] uppercase tracking-[.13em] text-[#2348C7]">Resources</p>
              <h1 className="font-[var(--font-source-serif)] mt-3 text-4xl leading-tight tracking-[-.02em] lg:text-5xl">
                Payroll Insights
              </h1>
            </div>
            <div>
              <p className="max-w-3xl text-[16px] leading-7 text-slate-600">
                Expert guides on Kenyan payroll compliance, statutory deductions, and
                everything you need to run accurate, stress-free payroll.
              </p>
            </div>
          </div>
        </div>
      </section>

      {loading && (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[#2348C7]" />
        </div>
      )}

      {/* Featured post */}
      {!loading && featured && (
        <section className="border-b border-[#DCE3EA] bg-white">
          <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
            <Link
              href={`/blog/${featured.slug}`}
              className="group grid grid-cols-1 lg:grid-cols-2"
            >
              {/* Gradient side */}
              <div
                className={`relative flex min-h-[280px] flex-col justify-end bg-gradient-to-br ${featured.coverGradient} p-8 lg:min-h-[380px] lg:p-10`}
              >
                <div className="absolute right-6 top-6">
                  <span className="mono text-[9px] uppercase tracking-[.13em] text-white/70">Featured</span>
                </div>
                <h2 className="max-w-lg font-[family-name:var(--font-source-serif)] text-2xl leading-tight text-white md:text-3xl">
                  {featured.title}
                </h2>
              </div>

              {/* Content side */}
              <div className="flex flex-col justify-between border-t border-[#DCE3EA] p-8 lg:border-t-0 lg:border-l lg:p-10">
                <div>
                  <div className="mono mb-4 flex flex-wrap items-center gap-4 text-[9px] uppercase tracking-[.1em] text-slate-400">
                    <span className="text-[#2348C7]">{featured.category}</span>
                    <span>
                      {new Date(featured.date).toLocaleDateString("en-KE", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                    <span>{featured.readTime}</span>
                  </div>

                  <p className="mb-6 text-[15px] leading-7 text-slate-600">
                    {featured.description}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {featured.tags.slice(0, 5).map((tag) => (
                      <span
                        key={tag}
                        className="border border-[#DCE3EA] px-2 py-0.5 text-[11px] text-slate-500"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mono mt-8 text-[10px] uppercase text-[#0F7B4D] opacity-0 transition group-hover:opacity-100">
                  Read full guide →
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* More posts */}
      {!loading && rest.length > 0 && (
        <section className="bg-[#F3F6FA]">
          <div className="mx-auto max-w-[1440px] px-5 py-16 sm:px-8 lg:px-12 lg:py-20">
            <div className="mono mb-8 text-[9px] uppercase tracking-[.13em] text-slate-400">More articles</div>
            <div className="border-t border-[#BDBBB3]">
              {rest.map((post) => (
                <Link
                  key={post.slug}
                  href={`/blog/${post.slug}`}
                  className="group grid w-full grid-cols-[88px_1fr_auto] gap-4 border-b border-[#DCE3EA] py-5 transition hover:bg-white/40"
                >
                  <span className="mono text-[9px] uppercase tracking-[.1em] text-slate-400">
                    {new Date(post.date).toLocaleDateString("en-KE", {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <div>
                    <p className="font-medium text-slate-900">{post.title}</p>
                    <p className="mt-1 text-[13px] text-slate-500 line-clamp-1">{post.description}</p>
                  </div>
                  <span className="mono self-center text-[10px] uppercase text-[#0F7B4D] opacity-0 transition group-hover:opacity-100">Read →</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer CTA */}
      <section className="border-t border-[#DCE3EA] bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 sm:px-8 lg:px-12 lg:py-20">
          <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
            <div>
              <p className="mono text-[10px] uppercase tracking-[.13em] text-[#2348C7]">Get started</p>
              <h2 className="font-[var(--font-source-serif)] mt-3 text-3xl leading-tight tracking-[-.02em]">
                Ready to calculate payroll?
              </h2>
            </div>
            <div>
              <p className="max-w-2xl text-[15px] leading-7 text-slate-600">
                Try SekaniPay&apos;s free calculator — instant, accurate, no sign-up required.
              </p>
              <Link
                href="/salary-calculator"
                className="ink-button mt-7 inline-block bg-[#2348C7] px-5 py-3 text-[13px] font-medium text-white hover:bg-[#18369B]"
              >
                Open Calculator →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
